import { Link, createFileRoute } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { ChapterPagination } from "~/components/chapter-pagination";
import { currentPageContextAtom } from "~/components/practice/chat-widget";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { MATERIAL_METADATA } from "~/data/material-metadata";
import {
	useGrammarByLevel,
	useKanjiByLevel,
	useVocabularyByLevel,
} from "~/lib/client/learn";
import { generateLearnLevelPageContext } from "~/lib/context-gen";
import type {
	GrammarContent,
	KanjiContent,
	Level,
	VocabularyContent,
} from "~/lib/server/db/schema/flashcard-schema";

export const Route = createFileRoute("/app/learn/jlpt/$level/")({
	component: RouteComponent,
	loader: async ({ context, params }) => {
		const level = params.level as Level;
		return {
			metadata: MATERIAL_METADATA[level],
		};
	},
});

const ITEMS_PER_PAGE = 5;

function JlptLevelSkeleton() {
	const ITEMS_PER_PAGE = 5;

	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 py-4">
			{/* Header */}
			<section className="mb-4">
				<Skeleton className="h-8 w-1/2 mb-2" /> {/* Title */}
				<Skeleton className="h-4 w-full mb-1" /> {/* Description line 1 */}
				<Skeleton className="h-4 w-full mb-1" /> {/* Description line 2 */}
				<Skeleton className="h-4 w-3/4" /> {/* Description line 3 */}
			</section>

			{/* Tabs */}
			<section className="w-full mt-4">
				<div className="h-10 w-full p-1 flex justify-between bg-muted rounded-md">
					{/* TabsList */}
					<Skeleton className="h-8 flex-1 mx-1" /> {/* TabTrigger 1 */}
					<Skeleton className="h-8 flex-1 mx-1" /> {/* TabTrigger 2 */}
					<Skeleton className="h-8 flex-1 mx-1" /> {/* TabTrigger 3 */}
					<Skeleton className="h-8 flex-1 mx-1" /> {/* TabTrigger 4 */}
				</div>
			</section>

			{/* Content Area (Accordion) */}
			<section className="flex flex-col gap-2 mt-4">
				{Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: skeleton component
						key={index}
						className="rounded-lg border border-pink-100 p-3 sm:p-4"
					>
						<Skeleton className="h-6 w-32 mb-3" /> {/* Chapter title */}
						<div className="space-y-2">
							{Array.from({ length: 3 }).map((_, itemIndex) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: skeleton component
									key={itemIndex}
									className="bg-white border border-pink-200 rounded-md p-2 sm:p-3"
								>
									<div className="grid grid-cols-[6rem_1fr] sm:grid-cols-[8rem_1fr] gap-2">
										<Skeleton className="h-12 w-full" />
										<div className="space-y-1">
											<Skeleton className="h-4 w-3/4" />
											<Skeleton className="h-3 w-full" />
											<Skeleton className="h-3 w-1/2" />
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				))}
			</section>

			{/* Pagination */}
			<section className="flex items-center justify-center gap-4 mt-4">
				<Skeleton className="h-10 w-24" /> {/* Previous Button */}
				<Skeleton className="h-6 w-16" /> {/* Page Indicator */}
				<Skeleton className="h-10 w-24" /> {/* Next Button */}
			</section>
		</div>
	);
}

function RouteComponent() {
	const { level } = Route.useParams();
	const { metadata } = Route.useLoaderData();

	return (
		<SuspenseFallback
			loading={<JlptLevelSkeleton />}
			error={
				<div className="p-8 text-red-500">
					An error occurred while loading JLPT {level.toUpperCase()} materials
				</div>
			}
		>
			<JlptLevelContent level={level as Level} metadata={metadata} />
		</SuspenseFallback>
	);
}

function JlptLevelContent({ level, metadata }: { level: Level; metadata: { title: string; description: string } }) {
	const setPageContext = useSetAtom(currentPageContextAtom);

	const { data: vocabularies = [] } = useVocabularyByLevel(level);
	const { data: grammars = [] } = useGrammarByLevel(level);
	const { data: kanjiGroups = [] } = useKanjiByLevel(level);

	const [currentVocabPage, setCurrentVocabPage] = useState(1);
	const [currentGrammarPage, setCurrentGrammarPage] = useState(1);
	const [currentKanjiPage, setCurrentKanjiPage] = useState(1);

	// Calculate the items to display for the current page
	const startIndexVocab = (currentVocabPage - 1) * ITEMS_PER_PAGE;
	const endIndexVocab = startIndexVocab + ITEMS_PER_PAGE;
	const paginatedVocabularies = vocabularies.slice(
		startIndexVocab,
		endIndexVocab,
	);

	const startIndexGrammar = (currentGrammarPage - 1) * ITEMS_PER_PAGE;
	const endIndexGrammar = startIndexGrammar + ITEMS_PER_PAGE;
	const paginatedGrammars = grammars.slice(startIndexGrammar, endIndexGrammar);

	const startIndexKanji = (currentKanjiPage - 1) * ITEMS_PER_PAGE;
	const endIndexKanji = startIndexKanji + ITEMS_PER_PAGE;
	const paginatedKanjiGroups = kanjiGroups.slice(
		startIndexKanji,
		endIndexKanji,
	);

	useEffect(() => {
		setPageContext({
			title: `JLPT ${level.toUpperCase()}`,
			slug: `/app/learn/jlpt/${level}`,
			contextData: generateLearnLevelPageContext({
				level,
				paginatedVocabCount: paginatedVocabularies.length,
				totalVocabCount: vocabularies.length,
				paginatedGrammarCount: paginatedGrammars.length,
				totalGrammarCount: grammars.length,
				paginatedKanjiCount: paginatedKanjiGroups.length,
				totalKanjiCount: kanjiGroups.length,
			}),
		});
	}, [
		setPageContext,
		level,
		paginatedVocabularies,
		vocabularies,
		paginatedGrammars,
		grammars,
		paginatedKanjiGroups,
		kanjiGroups,
	]);

	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 py-4">
			<section>
				<h1 className="text-xl lg:text-2xl font-semibold text-pink-600 mb-2">
					{metadata.title}
				</h1>
				<p className="text-sm lg:text-base text-muted-foreground mb-2">
					{metadata.description}
				</p>
				<p className="text-sm lg:text-base text-muted-foreground">
					Each section contains around 50 flashcards to make it manageable to
					learn and doesn't require a lot of time to review each session!
				</p>
			</section>

			<Tabs defaultValue="vocabulary" className="w-full mt-4">
				<TabsList className="w-full p-1">
					<TabsTrigger
						value="vocabulary"
						className="flex-1 data-[state=active]:bg-white data-[state=active]:text-pink-600 text-xs md:text-sm"
					>
						Vocabulary
					</TabsTrigger>
					<TabsTrigger
						value="grammar"
						className="flex-1 data-[state=active]:bg-white data-[state=active]:text-pink-600 text-xs md:text-sm"
					>
						Grammar
					</TabsTrigger>
					<TabsTrigger
						value="kanji"
						className="flex-1 data-[state=active]:bg-white data-[state=active]:text-pink-600 text-xs md:text-sm"
					>
						Kanji
					</TabsTrigger>
					<TabsTrigger
						value="quiz"
						className="flex-1 data-[state=active]:bg-white data-[state=active]:text-pink-600 text-xs md:text-sm"
					>
						Quiz
					</TabsTrigger>
				</TabsList>

				{/* Vocabulary Tab Content */}
				<TabsContent value="vocabulary">
					<Accordion type="single" collapsible>
						{paginatedVocabularies.map((group, groupIndex) => (
							<AccordionItem
								key={`vocab-group-${group[0]?.gid}`}
								value={groupIndex.toString()}
								className="rounded-lg px-3 sm:px-4 mb-2 border! border-pink-100"
							>
								<AccordionTrigger className="text-sm sm:text-base text-pink-800 py-2 sm:py-3">
									Chapter {startIndexVocab + groupIndex + 1}
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-2">
									{group.map((word) => {
										const content = word.content as VocabularyContent;
										let translations = content.translations
											.slice(0, 3)
											.map((t) => t.translation);
										if (translations.length === 1) {
											translations = translations[0].split(",");
										}
										return (
											<Link
												key={word.sid}
												preload="intent"
												to="/app/learn/jlpt/$level/vocabulary/$vocabId"
												params={{ vocabId: word.sid, level }}
												className="hover:opacity-90 transition-opacity"
											>
												<div className="bg-white border border-pink-200 rounded-md grid grid-cols-[6rem_1fr] sm:grid-cols-[8rem_1fr] items-start overflow-hidden hover:border-pink-300 transition-colors">
													<div className="flex flex-col items-center justify-center w-full h-full overflow-hidden border-r border-pink-200">
														<span className="text-xs py-1 text-pink-700/60 bg-white w-full h-full flex items-center justify-center">
															{content.readings?.[0]}
														</span>
														<Separator className="bg-pink-200" />
														<span className="text-pink-800 text-base sm:text-lg py-1 bg-purple-50 w-full h-full flex items-center justify-center">
															{content.kanji?.[0]?.kanji ?? word.reading}
														</span>
													</div>
													<div className="text-pink-600 text-sm saturate-50 p-2 sm:p-3">
														<ul className="text-xs sm:text-sm list-disc list-inside capitalize">
															{translations.slice(0, 3).map((translation) => (
																<li key={translation} className="truncate">
																	{translation.trim()}
																</li>
															))}
														</ul>
													</div>
												</div>
											</Link>
										);
									})}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
					<ChapterPagination
						totalItems={vocabularies.length}
						itemsPerPage={ITEMS_PER_PAGE}
						onPageChange={setCurrentVocabPage}
					/>
				</TabsContent>

				{/* Grammar Tab Content */}
				<TabsContent value="grammar">
					<Accordion type="single" collapsible>
						{paginatedGrammars.map((group, groupIndex) => (
							<AccordionItem
								key={`grammar-group-${group[0]?.gid}`}
								value={groupIndex.toString()}
								className="rounded-lg px-3 sm:px-4 mb-2 border! border-pink-100"
							>
								<AccordionTrigger className="text-sm sm:text-base text-pink-800 py-2 sm:py-3">
									Chapter {startIndexGrammar + groupIndex + 1}
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-2">
									{group.map((grammar) => {
										const content = grammar.content as GrammarContent;
										return (
											<Link
												key={grammar.sid}
												preload="intent"
												to="/app/learn/jlpt/$level/grammar/$grammarId"
												params={{ grammarId: grammar.sid, level }}
												className="hover:opacity-90 transition-opacity rounded-md"
											>
												<div className="bg-white border border-pink-200 rounded-md p-2 sm:p-3 hover:border-pink-300 transition-colors">
													<h3 className="text-pink-800 text-sm sm:text-base">
														{grammar.reading}
													</h3>
													<div className="text-xs sm:text-sm">
														<span className="text-pink-600">Meaning:</span>{" "}
														<span className="text-pink-800/60">
															{content.meaning.meaning}
														</span>
													</div>
													{content.phrases && content.phrases.length > 0 && (
														<>
															<h4 className="text-pink-600 text-xs sm:text-sm">
																Examples:
															</h4>
															<ul className="list-disc list-inside text-xs sm:text-sm">
																{content.phrases?.slice(0, 1).map((ex) => (
																	<li key={ex.phrase} className="truncate">
																		{ex.phrase} - {ex.translation}
																	</li>
																))}
															</ul>
														</>
													)}
												</div>
											</Link>
										);
									})}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
					<ChapterPagination
						totalItems={grammars.length}
						itemsPerPage={ITEMS_PER_PAGE}
						onPageChange={setCurrentGrammarPage}
					/>
				</TabsContent>

				{/* Kanji Tab Content */}
				<TabsContent value="kanji">
					<Accordion type="single" collapsible>
						{paginatedKanjiGroups.map((group, groupIndex) => (
							<AccordionItem
								key={`kanji-group-${group[0]?.gid}`}
								value={groupIndex.toString()}
								className="rounded-lg px-3 sm:px-4 mb-2 border! border-pink-100"
							>
								<AccordionTrigger className="text-sm sm:text-base text-pink-800 py-2 sm:py-3">
									Chapter {startIndexKanji + groupIndex + 1}
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-2">
									{group.map((kanji) => {
										const content = kanji.content as KanjiContent;
										return (
											<Link
												key={kanji.sid}
												preload="intent"
												to="/app/learn/jlpt/$level/kanji/$kanjiId"
												params={{ kanjiId: kanji.sid, level }}
												className="hover:opacity-90 transition-opacity rounded-md"
											>
												<div className="bg-white border border-pink-200 rounded-md grid grid-cols-[6rem_1fr] sm:grid-cols-[8rem_1fr] items-start overflow-hidden hover:border-pink-300 transition-colors">
													<div className="flex flex-col items-center justify-center w-full h-full overflow-hidden border-r border-pink-200">
														<span className="text-xs py-1 text-pink-700/60 bg-white w-full h-full flex items-center justify-center">
															{content?.onyomi?.[0] || "-"}
														</span>
														<Separator className="bg-pink-200" />
														<span className="text-pink-800 text-base sm:text-lg py-1 bg-purple-50 w-full h-full flex items-center justify-center">
															{kanji.reading}
														</span>
													</div>
													<div className="text-pink-600 text-sm saturate-50 p-2 sm:p-3">
														<ul className="text-xs sm:text-sm list-disc list-inside capitalize">
															{content?.meanings
																?.slice(0, 3)
																.map((meaning: string) => (
																	<li key={meaning} className="truncate">
																		{meaning.trim()}
																	</li>
																))}
														</ul>
													</div>
												</div>
											</Link>
										);
									})}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
					<ChapterPagination
						totalItems={kanjiGroups.length}
						itemsPerPage={ITEMS_PER_PAGE}
						onPageChange={setCurrentKanjiPage}
					/>
				</TabsContent>

				{/* Quiz Tab Content */}
				<TabsContent value="quiz">
					<div className="bg-white rounded-lg p-6 border border-pink-100">
						<h2 className="text-lg font-semibold text-pink-800 mb-2">
							Ready to Test Your Knowledge?
						</h2>
						<p className="text-sm text-muted-foreground mb-4">
							Challenge yourself with a quiz covering vocabulary, grammar, and
							reading comprehension from JLPT {level.toUpperCase()}. Each quiz
							is dynamically generated to help you practice effectively.
						</p>
						<Button asChild>
							<Link
								to="/app/learn/jlpt/$level/quiz"
								params={{ level }}
								className="gap-2"
							>
								Start Quiz <ArrowRight className="w-4 h-4" />
							</Link>
						</Button>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	);
}
